import { Callback } from '../../../types';
import { isMobile } from '../../../utils';
import * as Video from 'twilio-video';
import { ConnectOptions, LocalTrack, Room } from 'twilio-video';
import { VideoRoomMonitor } from '@twilio/video-room-monitor';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../../state';
import * as Sentry from '@sentry/react';

// @ts-ignore
window.TwilioVideo = Video;

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const { setTaskSid } = useAppState();
  const optionsRef = useRef(options);

  useEffect(() => {
    // This allows the connect function to always access the most recent version of the options object. This allows us to
    // reliably use the connect function at any time.
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback(
    token => {
      setIsConnecting(true);
      return Video.connect(token, { ...optionsRef.current, tracks: localTracks }).then(
        newRoom => {
          Sentry.addBreadcrumb({
            type: 'debug',
            category: '###RoomConnected',
            level: Sentry.Severity.Info,
            data: newRoom,
          });
          setRoom(newRoom);

          sessionStorage.setItem('videoRoomSid', newRoom.sid);

          console.log('### NewRoom', newRoom);
          VideoRoomMonitor.registerVideoRoom(newRoom);
          const disconnect = () => {
            // console.log('### RoomDisconnectEvent', room);
            Sentry.addBreadcrumb({
              type: 'debug',
              category: '###RoomDisconnectEvent',
              level: Sentry.Severity.Info,
              data: newRoom || undefined,
            });
            return newRoom.disconnect();
          };

          // When doctors use this app it is embedded in an iframe by plugin-med (our Flex plugin). It answers the
          // getTaskSid request below with { taskSid }, and posts 'disconnect' when the task is transferred.
          // Only messages from that parent window are handled: browser extensions (e.g. React DevTools) and the
          // dev server also post messages to this window, and treating those as 'disconnect' used to drop the
          // call right after joining.
          const embedder = window.self !== window.top ? window.parent : null;
          const handleEmbedderMessage = (e: MessageEvent) => {
            if (!embedder || e.source !== embedder) return;
            if (e.data?.taskSid) {
              setTaskSid(e.data.taskSid);
            } else if (e.data === 'disconnect') {
              disconnect();
            }
          };

          if (embedder) {
            embedder.postMessage({ getTaskSid: true }, '*');
          }
          // This app adds around 16 'participantDisconnected' listeners to the room object (they are removed again
          // when their components unmount), which can trigger a warning from the EventEmitter object. Here we
          // increase the max listeners to suppress the warning.
          newRoom.setMaxListeners(20);

          newRoom.once('disconnected', () => {
            // Reset the room only after all other `disconnected` listeners have been called.
            setTimeout(() => setRoom(null));
            window.parent.postMessage({ endCall: true }, '*');
            window.removeEventListener('beforeunload', disconnect);
            window.removeEventListener('message', handleEmbedderMessage);

            if (isMobile) {
              window.removeEventListener('pagehide', disconnect);
            }
          });

          // @ts-ignore
          window.twilioRoom = newRoom;

          newRoom.localParticipant.videoTracks.forEach(publication =>
            // All video tracks are published with 'low' priority because the video track
            // that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            publication.setPriority('low')
          );

          setIsConnecting(false);

          // Add a listener to disconnect from the room when a user closes their browser
          window.addEventListener('beforeunload', disconnect);

          window.addEventListener('message', handleEmbedderMessage);

          if (isMobile) {
            // Add a listener to disconnect from the room when a mobile user closes their browser
            window.addEventListener('pagehide', disconnect);
          }
        },
        error => {
          onError(error);
          setIsConnecting(false);
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localTracks, onError, setTaskSid]
  );

  return { room, isConnecting, connect };
}
